// src/pages/Tickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  FaTicketAlt,
  FaPlusCircle,
  FaInbox,
  FaSyncAlt,
  FaExclamationCircle,
  FaTimes,
  FaChevronRight,
} from "react-icons/fa";
import { authFetch } from "../utils/api";

/* ----------------------------- SOLID MODAL ----------------------------- */
const Modal = ({ open, onClose, children, titleId, wide }) => {
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prev;
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  // Rendered via portal straight onto <body>, outside the app's own
  // .leo-shell-v2 root wrapper -- so it needs its own .leo-shell-v2
  // ancestor (same approach Sidebar/Topbar use) for the shared tokens
  // and .modal-* classes to apply.
  return createPortal(
    <div className="leo-shell-v2" style={{ minHeight: 0 }}>
      <div
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="modal-overlay"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`modal-panel panel${wide ? " lg" : ""}`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ----------------------------- tiny ui bits ---------------------------- */
const STATUS_PILL = {
  Open: "pill-warning",
  Pending: "pill-info",
  Review: "pill-info",
  Closed: "pill-success",
};

const StatusPill = ({ status }) => (
  <span className={`pill ${STATUS_PILL[status] || "pill-info"}`}>
    <span className="dot" />
    {status}
  </span>
);

const STATUS_CHIPS = [
  { value: "all", label: "All" },
  { value: "Open", label: "Open" },
  { value: "Pending", label: "Pending" },
  { value: "Review", label: "Review" },
  { value: "Closed", label: "Closed" },
];

const SkeletonRow = () => (
  <tr>
    <td>
      <div className="skeleton" style={{ height: 13, width: "60%", marginBottom: 8 }} />
      <div className="skeleton" style={{ height: 10, width: "35%" }} />
    </td>
    <td>
      <div className="skeleton" style={{ height: 12, width: "85%" }} />
    </td>
    <td>
      <div className="skeleton" style={{ height: 20, width: 74, borderRadius: 999 }} />
    </td>
    <td>
      <div className="skeleton" style={{ height: 12, width: "65%" }} />
    </td>
    <td />
  </tr>
);

/* -------------------------------- PAGE --------------------------------- */
export default function Tickets() {
  // pull user from localStorage
  const user =
    JSON.parse(localStorage.getItem("user")) || {
      name: "Unknown User",
      SLIITID: "",
      role: "Member",
      image: "/user.png",
    };

  /* data & ui state */
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [netErr, setNetErr] = useState("");

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("new");

  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "" });

  const [reviewModal, setReviewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");

  /* fetch my tickets */
  const fetchTickets = () => {
    if (!user?.SLIITID) {
      setTickets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNetErr("");

    fetch("https://dev.sliitleo.org/Backend/getmytickets.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sliitid: user.SLIITID }),
    })
      .then(async (res) => {
        const text = await res.text();
        try { return { ok: res.ok, json: JSON.parse(text) }; }
        catch { throw new Error(text || "Failed to load tickets"); }
      })
      .then(({ ok, json }) => {
        if (!ok || !json?.success) throw new Error(json?.error || "Failed to load tickets");
        const mine = (json.tickets || []).filter(
          (t) => String(t.user_id) === String(user.SLIITID).replace(/\D+/g, "")
        );
        setTickets(mine);
      })
      .catch((err) => {
        setNetErr(err.message || "Network error");
        setTickets([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTickets(); /* eslint-disable-next-line */ }, [user.SLIITID]);

  /* search/filter/sort */
  const filtered = useMemo(() => {
    let out = [...tickets];
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(
        (t) =>
          String(t.subject || "").toLowerCase().includes(q) ||
          String(t.description || "").toLowerCase().includes(q) ||
          String(t.id || "").includes(q)
      );
    }
    if (statusFilter !== "all") out = out.filter((t) => String(t.status) === statusFilter);
    out.sort((a, b) => {
      const da = new Date(a.created_at || a.createdAt || 0).getTime();
      const db = new Date(b.created_at || b.createdAt || 0).getTime();
      return sortOrder === "old" ? da - db : db - da;
    });
    return out;
  }, [tickets, query, statusFilter, sortOrder]);

  /* create ticket */
  const onCreateChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onCreateSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) return;
    setSaving(true);
    authFetch("https://dev.sliitleo.org/Backend/create_my_tickets.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: user.SLIITID,
        user_name: user.name || "Unknown User",
        subject: form.subject.trim(),
        description: form.description.trim(),
      }),
    })
      .then((r) => r.json())
      .then((resp) => {
        if (!resp?.success) throw new Error(resp?.error || "Failed to create ticket");
        setForm({ subject: "", description: "" });
        setShowCreate(false);
        fetchTickets();
      })
      .catch((err) => {
        alert(err.message || "Failed to create ticket");
      })
      .finally(() => setSaving(false));
  };

  /* open review */
  const openReview = (ticketId) => {
    setReviewModal(true);
    setReviewLoading(true);
    setReviewError("");
    setSelectedTicket(null);

    fetch("https://dev.sliitleo.org/Backend/get_ticket_review.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket_id: ticketId, sliitid: user.SLIITID }),
    })
      .then((r) => r.json())
      .then((resp) => {
        if (!resp?.success) throw new Error(resp?.error || "Could not load ticket");
        setSelectedTicket(resp.ticket);
      })
      .catch((err) => setReviewError(err.message || "Could not load ticket"))
      .finally(() => setReviewLoading(false));
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* HEADER */}
        <div className="page-head">
          <div>
            <div className="eyebrow">Support</div>
            <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FaTicketAlt size={18} /> My Tickets
            </h1>
            <p className="page-desc">Raise a ticket and track responses from the admin team.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-ghost" onClick={fetchTickets}>
              <FaSyncAlt size={13} /> Refresh
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <FaPlusCircle size={13} /> Raise Ticket
            </button>
          </div>
        </div>

        {/* FILTERS */}
        <div className="panel" style={{ padding: "var(--sp-4)", marginBottom: "var(--sp-5)" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-4)" }}>
            <div className="field" style={{ flex: "1 1 260px", marginBottom: 0 }}>
              <label className="field-label" htmlFor="ticket-search">Search tickets</label>
              <input
                id="ticket-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by subject, description, or #ID…"
              />
            </div>
            <div className="field" style={{ minWidth: 180, marginBottom: 0 }}>
              <label className="field-label" htmlFor="ticket-sort">Sort by</label>
              <select id="ticket-sort" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="new">Newest first</option>
                <option value="old">Oldest first</option>
              </select>
            </div>
          </div>

          <div
            className="filter-row"
            role="group"
            aria-label="Filter by status"
            style={{ flexWrap: "wrap", marginTop: "var(--sp-4)" }}
          >
            {STATUS_CHIPS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`chip${statusFilter === value ? " active" : ""}`}
                aria-pressed={statusFilter === value}
                onClick={() => setStatusFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ERROR BAR */}
        {netErr && (
          <div className="error-banner" role="alert" style={{ marginBottom: "var(--sp-4)" }}>
            <FaExclamationCircle size={14} />
            <span className="truncate">{netErr}</span>
          </div>
        )}

        {/* LIST PANEL */}
        <div className="panel">
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th scope="col">Subject</th>
                  <th scope="col">Description</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {loading && (<><SkeletonRow /><SkeletonRow /><SkeletonRow /></>)}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty-state">
                        <div className="empty-icon"><FaInbox size={18} /></div>
                        <h3>No tickets yet</h3>
                        <p>Raise a ticket and it will show up here once submitted.</p>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && filtered.map((t, idx) => (
                  <tr
                    key={`trow-${t.id}-${idx}`}
                    style={{ cursor: "pointer" }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View ticket: ${t.subject}`}
                    onClick={() => openReview(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openReview(t.id);
                      }
                    }}
                  >
                    <td>
                      <div className="proj-name">
                        {t.subject}{" "}
                        <span className="num" style={{ color: "var(--text-3)", fontWeight: 400 }}>
                          #{t.id}
                        </span>
                      </div>
                      <div className="proj-cat">by {t.user_name || "You"}</div>
                    </td>
                    <td className="truncate" style={{ maxWidth: 360 }} title={t.description}>
                      {t.description}
                    </td>
                    <td>
                      <StatusPill status={t.status} />
                    </td>
                    <td className="num" style={{ color: "var(--text-2)" }}>
                      {new Date(t.created_at || t.createdAt).toLocaleString()}
                    </td>
                    <td className="row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Open ticket #${t.id}`}
                        onClick={(e) => { e.stopPropagation(); openReview(t.id); }}
                      >
                        <FaChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ------------------------ CREATE MODAL ------------------------ */}
        <Modal open={showCreate} onClose={() => setShowCreate(false)} titleId="raiseTicketTitle" wide>
          <div className="modal-head">
            <h3 id="raiseTicketTitle" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FaTicketAlt size={14} /> Raise a New Ticket
            </h3>
            <button type="button" className="icon-btn" onClick={() => setShowCreate(false)} aria-label="Close">
              <FaTimes size={14} />
            </button>
          </div>

          <form onSubmit={onCreateSubmit}>
            <div className="modal-body">
              <div className="field">
                <label className="field-label" htmlFor="ticket-subject">
                  Subject<span className="req">*</span>
                </label>
                <input
                  id="ticket-subject"
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={onCreateChange}
                  placeholder="Enter subject"
                  required
                  autoFocus
                />
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="ticket-description">
                  Description<span className="req">*</span>
                </label>
                <textarea
                  id="ticket-description"
                  name="description"
                  value={form.description}
                  onChange={onCreateChange}
                  rows={6}
                  placeholder="Describe your problem..."
                  required
                />
              </div>
            </div>

            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving && <span className="spinner" />} {saving ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </Modal>

        {/* ------------------------ REVIEW MODAL ------------------------ */}
        <Modal open={reviewModal} onClose={() => setReviewModal(false)} titleId="ticketReviewTitle" wide>
          <div className="modal-head">
            <h3 id="ticketReviewTitle" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FaTicketAlt size={14} /> Ticket Review
            </h3>
            <button type="button" className="icon-btn" onClick={() => setReviewModal(false)} aria-label="Close">
              <FaTimes size={14} />
            </button>
          </div>

          <div className="modal-body">
            {reviewLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-2)" }}>
                <span className="spinner" /> Loading…
              </div>
            )}

            {reviewError && (
              <div className="error-banner" role="alert">
                <FaExclamationCircle size={14} />
                <span>{reviewError}</span>
              </div>
            )}

            {selectedTicket && !reviewLoading && !reviewError && (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      display: "grid",
                      placeItems: "center",
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: 999,
                      background: "var(--surface-2)",
                      color: "var(--accent)",
                      fontSize: 20,
                    }}
                  >
                    <FaTicketAlt />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span className="truncate">{selectedTicket.subject}</span>
                      <span className="num" style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 400 }}>
                        #{selectedTicket.id}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>{selectedTicket.user_name}</div>
                  </div>
                </div>

                <div>
                  <div className="field-label" style={{ marginBottom: 6 }}>Description</div>
                  <div style={{ color: "var(--text-1)", fontSize: 13, whiteSpace: "pre-line" }}>
                    {selectedTicket.description}
                  </div>
                </div>

                <div>
                  <div className="field-label" style={{ marginBottom: 6 }}>Response</div>
                  <div style={{ color: "var(--text-1)", fontSize: 13, whiteSpace: "pre-line" }}>
                    {selectedTicket.review || "—"}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 10,
                    paddingTop: "var(--sp-3)",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <StatusPill status={selectedTicket.status} />
                  <span className="num" style={{ fontSize: 11, color: "var(--text-3)" }}>
                    Created: {selectedTicket.created_at || selectedTicket.createdAt}
                  </span>
                  <span className="num" style={{ fontSize: 11, color: "var(--success)" }}>
                    Reviewed: {selectedTicket.review_date || "—"}
                  </span>
                  {selectedTicket.review_name && (
                    <span style={{ fontSize: 11, color: "var(--text-2)" }}>
                      Review by: {selectedTicket.review_name}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      </main>
    </div>
  );
}
